import MenuIcon from '@mui/icons-material/Menu';
import Warning from '@mui/icons-material/Warning';
import { AppBar, Avatar, Box, IconButton, Link, Menu, MenuItem, Toolbar, Typography } from "@mui/material";
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from './AuthContext';

export const ACCOUNT_DISABLED_MESSAGE =
    'Your account has been disabled. Your subscription has been cancelled and you will not be charged. If you believe this was a mistake, please use the contact form';

export default function LawAppBar() {
    const [anchorEl, setAnchorEl] = useState(null);
    const { user, isAuthenticated, logout, cookies } = useAuth();
    const navigate = useNavigate();

    const picture = user?.picture || cookies.picture;
    const givenName = user?.given_name || cookies.given_name;
    const accountDisabled = user?.accountDisabled === true;

    return (
        <AppBar
            position="static"
            sx={{
                background: 'var(--background-color)',
                boxShadow: 'var(--shadow-subtle)',
                borderBottom: '1px solid var(--border-color)',
                color: 'var(--text-primary)'
            }}
        >
            <Toolbar
                variant="dense"
                sx={{
                    minHeight: '48px',
                    padding: '0 20px'
                }}
            >
                <IconButton
                    onClick={() => navigate('/mycases')}
                    size="small"
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    sx={{
                        mr: 1.5,
                        color: 'var(--text-secondary)',
                        '&:hover': {
                            background: 'var(--surface-color)',
                            color: 'var(--text-primary)'
                        }
                    }}
                >
                    <MenuIcon />
                </IconButton>

                <Box
                    onClick={() => navigate('/mycases')}
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer'
                    }}
                >
                    <img
                        src="/AttorneyAideLogo.png"
                        alt="AttorneyAide"
                        style={{
                            height: '32px',
                            maxWidth: '200px',
                            objectFit: 'contain'
                        }}
                    />
                </Box>

                {isAuthenticated && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                            src={picture || undefined}
                            onClick={handleMenu}
                            alt={givenName || 'profile'}
                            referrerPolicy="no-referrer"
                            aria-controls="profile-menu"
                            sx={{
                                cursor: "pointer",
                                width: 32,
                                height: 32,
                                border: '1px solid var(--border-color)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    transform: 'scale(1.05)',
                                    border: '1px solid var(--text-secondary)'
                                }
                            }}
                        />
                        <Menu
                            id="profile-menu"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            PaperProps={{
                                sx: {
                                    mt: 1,
                                    minWidth: 120,
                                    borderRadius: 1,
                                    boxShadow: 'var(--shadow-light)',
                                    border: '1px solid var(--border-color)',
                                    '& .MuiMenuItem-root': {
                                        fontSize: '0.85rem',
                                        fontWeight: 400,
                                        color: 'var(--text-primary)',
                                        padding: '8px 16px',
                                        '&:hover': {
                                            background: 'var(--surface-color)'
                                        }
                                    }
                                }
                            }}
                        >
                            <MenuItem onClick={onAccountClicked}>
                                Account & Billing
                            </MenuItem>
                            <MenuItem onClick={onLogoutClicked}>
                                Logout
                            </MenuItem>
                        </Menu>
                    </Box>
                )}
            </Toolbar>
            {isAuthenticated && accountDisabled && (
                <Box
                    role="status"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        padding: '8px 20px',
                        backgroundColor: 'rgba(244, 67, 54, 0.08)',
                        borderTop: '1px solid rgba(244, 67, 54, 0.2)',
                    }}
                >
                    <Warning
                        sx={{
                            color: 'var(--error-color)',
                            fontSize: '18px',
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        sx={{
                            color: 'var(--error-color)',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            lineHeight: 1.4,
                        }}
                    >
                        {ACCOUNT_DISABLED_MESSAGE}{' '}
                        <Link
                            component={RouterLink}
                            to="/contact"
                            sx={{
                                fontWeight: 700,
                                textDecoration: 'underline',
                                color: 'inherit',
                                '&:hover': {
                                    opacity: 0.85,
                                },
                            }}
                        >
                            at /contact
                        </Link>
                        .
                    </Typography>
                </Box>
            )}
        </AppBar>
    );

    function handleMenu(event) {
        setAnchorEl(event.currentTarget);
    }

    function handleClose() {
        setAnchorEl(null);
    }

    function onAccountClicked() {
        setAnchorEl(null);
        navigate('/account');
    }

    async function onLogoutClicked() {
        setAnchorEl(null);
        await logout();
        navigate('/mycases');
    }
}
